import { AxiosResponse } from "axios";
import UserGroup from "../../pages/Admin/AdminUserOverview/userGroups";
import ApiResponse from "../models/ApiResponse";
import PagedResponse from "../models/PagedResponse";
import DailyUserReponse from "../models/statistics/DailyUserRespone";
import Api from "./Api";

type CSCLaboratory = TestRequestRaw.Schemas.CSCLaboratory;

export type UserInfo = TestRequestRaw.Schemas.UserInformation;
export type UserReferenceResponse = Required<TestRequestRaw.Schemas.UserReferenceResponse>;

export interface Metadata {
  key: string;
  value: number;
}
export type ActiveUsersObj = { [key: string]: Metadata };

export type LabLocation = "EUROPEAN_UNION" | "MIXED" | "NONE" | "NON_EUROPEAN_UNION" | "_UNDEFINED";

export type UserCountByLabLocation = {
  [L in LabLocation]: number;
};

export type ActiveUsersByLabLocation = Record<string, UserCountByLabLocation>;

class UserInfoApi {
  constructor(private api: Api) {}

  public getUserInfo(): Promise<AxiosResponse<UserInfo>> {
    return this.api.get("/test-request/me");
  }

  public getActiveUsers(lastXDays?: number): Promise<AxiosResponse<ActiveUsersObj>> {
    const lastXDaysParam = `?lastXDays=${lastXDays}`;
    return this.api.get(`/test-request/v2/active-users${lastXDays ? lastXDaysParam : ""}`);
  }

  public getActiveUsersDateRange(dateRange?: { from: string; to: string }): ApiResponse<DailyUserReponse> {
    const url = "/test-request/v2/active-users";
    const query = dateRange && new URLSearchParams(dateRange);
    return this.api.get(query ? `${url}?${query}` : url);
  }

  public getAssignableUsers(
    requestId: string,
    cscLaboratory: CSCLaboratory | false,
    searchTerm?: string,
    pageIndex?: number,
    pageSize?: number,
  ): Promise<AxiosResponse<PagedResponse<UserReferenceResponse>>> {
    const query = new URLSearchParams(searchTerm && { searchTerm });
    if (pageIndex != null) {
      query.append("pageIndex", String(pageIndex));
    }
    if (pageSize != null) {
      query.append("pageSize", String(pageSize));
    }
    if (cscLaboratory) {
      query.append("cscLaboratory", cscLaboratory);
    }
    return this.api.get(`/test-request/v2/testrequests/${requestId}/assignable-users?${query}`);
  }

  public getAllUsers(
    filter?: string,
    cscLaboratory?: CSCLaboratory,
    onlyApprovers?: boolean,
    pageIndex?: number,
    pageSize?: number,
  ): Promise<AxiosResponse<PagedResponse<UserReferenceResponse>>> {
    const query = new URLSearchParams({ filter: filter || "" });
    if (cscLaboratory) {
      query.append("cscLaboratory", cscLaboratory);
    }
    if (onlyApprovers) {
      query.append("onlyApprovers", "true");
    }
    if (pageIndex != null) {
      query.append("pageIndex", String(pageIndex));
    }
    if (pageSize != null) {
      query.append("pageSize", String(pageSize));
    }

    return this.api.get(`/test-request/v2/user?${query}`);
  }

  public changeUserGroups(userId: number, payload: UserGroup[]): ApiResponse<UserReferenceResponse> {
    return this.api.put(`/test-request/v2/users/${userId}/groups`, payload);
  }

  public changeUserLaboratories(userId: number, payload: CSCLaboratory[]): ApiResponse<UserReferenceResponse> {
    return this.api.put(`/test-request/v2/users/${userId}/labs`, payload);
  }

  public changeManagedLaboratories(
    userId: number,
    payload: TestRequestRaw.Schemas.ManagedLaboratoryRequest,
  ): ApiResponse<TestRequestRaw.Schemas.LaboratoryResponse[]> {
    return this.api.put(`/test-request/v2/users/${userId}/managedLabs`, payload);
  }
}

export default UserInfoApi;
